<template>
  <div>
    <mds-table :class="$style['advisory-fee-table']" row-numbers>
      <mds-thead :nested-headers="showAssetBaseFee ? 1 : undefined">
        <template v-slot:mds-thead-group-1>
          <mds-th colspan="1"></mds-th>
          <mds-th :colspan="assetBaseFeeColspan" scope="colgroup">{{
            translate('annualAssetBased')
          }}</mds-th>
        </template>
        <mds-th
          v-for="(header, index) in headers"
          :key="index"
          :style="header.style"
          :class="$style['advisory-fee-table__header-cell']"
        >
          {{ header.text }}
          <MoreInfo
            :show-info-icons="!!header.showInfoIcons"
            :show-learn-more="false"
            :abbr-content="header.infoIconContent"
          >
          </MoreInfo>
        </mds-th>
      </mds-thead>
      <mds-tbody>
        <mds-tr
          v-for="(security, index) in advisoryFeeSecurities"
          :key="index"
          :row-number="index + 1"
        >
          <mds-td v-for="(header, colIndex) in headers" :key="colIndex" :style="header.style">
            <template v-if="header.fieldName === 'annualFeeType'">
              <template v-if="isSecurityFeeDataPointExist(security, header.fieldName)">
                <mds-select
                  v-model.number="security.fees[header.fieldName]"
                  hidden-label
                  :label="header.text"
                  :options="annualFeeTypeOptions"
                  @change="handleSecurityAnnualFeeTypeChange(security)"
                ></mds-select>
              </template>
              <span v-else>
                {{ '&mdash;' }}
              </span>
            </template>
            <template v-else-if="header.fieldName === 'clickToAddColumn'">
              <fee-choice
                v-if="isMaxOrScheduleType(security)"
                :mwc-id="getMwcIdForChild('fee-choice-portfolio')"
                level="holding"
                :security-id="security.securityId"
                :fee-schedule-id="getSecurityFeeSchedule(security)"
                :advisory-fee-schedule="security.advisoryFeeSchedule"
                :environment="environment"
                :user-info="userInfo"
                :asset-based-fee-config="assetBasedFeeConfig"
                :all-fee-schedule-list="allFeeScheduleList"
                @fee-schedules-updated="$emit('fee-schedules-updated', $event)"
                @update-fee-schedule-id="handleFeeScheduleChange"
                @before-close="$emit('before-close', $event)"
              />
              <span v-else>
                {{ '&mdash;' }}
              </span>
            </template>
            <template v-else-if="header.fieldName === 'annualFeeAmount'">
              <hypo-number-input
                v-if="isFlat(security)"
                size="small"
                hidden-label
                is-percent
                :label="header.text"
                :saved-value="security.fees.annualFeeAmount"
                :allow-leave-blank="false"
                @update-saved-value="handleSecurityAnnualFeePercentChanged($event, security)"
              >
              </hypo-number-input>
              <hypo-number-input
                v-else-if="isMaxFee(security)"
                size="small"
                hidden-label
                is-percent
                :range="getMaxFeeRange(security)"
                :label="header.text"
                :readonly="isMaxFeeReadOnly(security)"
                :saved-value="getMaxFee(security)"
                :allow-leave-blank="false"
                @input-invalid="triggerAlert(security, header.fieldName)"
                @update-saved-value="handleSecurityMaxFeeChanged($event, security)"
              >
              </hypo-number-input>
              <hypo-number-input
                v-else-if="isHistoricMaxFeeDiscountApplicable(security)"
                :id="'historic-max-fee-input-' + index"
                size="small"
                hidden-label
                is-percent
                :range="getHistoricMaxFeeRange(security)"
                :label="header.text"
                :saved-value="getUserInputFeeForHistoricMaxFee(security)"
                :placeholder="translate('enterToChange')"
                @input-invalid="triggerAlert(security, header.fieldName)"
                @update-saved-value="handleSecurityMaxFeeChanged($event, security)"
                @focus="$set(historicMaxFeeInputPopoverFlags, index, true)"
                @blur="$set(historicMaxFeeInputPopoverFlags, index, false)"
              >
              </hypo-number-input>
              <span v-else>
                {{ '&mdash;' }}
              </span>
              <mds-popover
                v-model="historicMaxFeeInputPopoverFlags[index]"
                :triggered-by="'historic-max-fee-input-' + index"
                :position="['bottom-center', 'top-center']"
              >
                {{ translate('historicMaxFeeInputPopoverMsg') }}
              </mds-popover>
            </template>
            <template v-else-if="header.fieldName === 'discount'">
              <hypo-number-input
                v-if="isDiscountAllowedForMaxOrSchedule(security)"
                size="small"
                hidden-label
                is-percent
                :range="getDiscountRange(security)"
                :label="header.text"
                :saved-value="getDiscount(security)"
                :allow-leave-blank="false"
                @input-invalid="triggerAlert(security, header.fieldName)"
                @update-saved-value="handleSecurityFeeDiscountChanged($event, security)"
              >
              </hypo-number-input>
              <span v-else>
                {{ '&mdash;' }}
              </span>
            </template>
            <template v-else-if="header.fieldName === 'managerFee'">
              <ManagerFeeRender
                :mwc-id="getMwcIdForChild('ManagerFeeViewer')"
                :security-type="security.securityType"
                :fee-name="security.securityName"
                :schedule="security.advisoryFeeSchedule"
                :manager-fee="getFlatManagerFeeAmount(security)"
                :historic-manager-fee="getHistoricManagerFee(security)"
              />
            </template>
            <template v-else-if="header.fieldName === 'totalAnnualMaxFee'">
              <AnnualMaxFeeTotalRender
                :mwc-id="getMwcIdForChild('AnnualMaxFeeViewer')"
                :security-type="security.securityType"
                :fee-name="security.securityName"
                :schedule="security.advisoryFeeSchedule"
                :manager-fee="getFlatManagerFeeAmount(security)"
                :historic-manager-fee="getHistoricManagerFee(security)"
              />
            </template>
            <template
              v-else-if="['totalAnnualFee', 'totalAnnualProposedFee'].includes(header.fieldName)"
            >
              <AnnualFeeTotalRender
                :mwc-id="getMwcIdForChild('AnnualFeeViewer')"
                :annual-max-fee="security.fees.annualMaxFee"
                :annual-fee-amount="security.fees.annualFeeAmount"
                :annual-fee-type="security.fees.annualFeeType"
                :security-type="security.securityType"
                :fee-name="security.securityName"
                :schedule="security.advisoryFeeSchedule"
                :manager-fee="getFlatManagerFeeAmount(security)"
                :historic-manager-fee="getHistoricManagerFee(security)"
              />
            </template>
            <template v-else-if="header.fieldName === 'annualFeeChargeFrequency'">
              <template v-if="isSecurityFeeDataPointExist(security, header.fieldName)">
                <mds-select
                  v-model.number="security.fees[header.fieldName]"
                  hidden-label
                  :label="header.text"
                  :options="billFrequencyOptions"
                  @change="emitSecuritiesData"
                ></mds-select>
              </template>
              <span v-else>
                {{ '&mdash;' }}
              </span>
            </template>
            <template v-else-if="header.fieldName === 'securityName'">
              {{ security[header.fieldName] }}
            </template>
            <template v-else>
              {{ security[header.fieldName] }}
            </template>
          </mds-td>
        </mds-tr>
      </mds-tbody>
    </mds-table>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import MdsSelect from '@mds/select';
import MdsPopover from '@mds/popover';
import MoreInfo from 'hypo-shared-utils/src/components/MoreInfo';
import HypoNumberInput from 'hypo-shared-utils/src/components/HypoNumberInput';
import FeeChoice from '../shared/fee-choice/FeeChoice';
import ManagerFeeRender from './column-render/ManagerFeeRender';
import AnnualFeeTotalRender from './column-render/AnnualFeeTotalRender';
import AnnualMaxFeeTotalRender from './column-render/AnnualMaxFeeTotalRender';
import { roundNumber, isMaxFee, isHistoricMaxFee, getFeeChoiceLabelKey } from '../../utils/utils';
import {
  getMaxFeeRange,
  getHistoricMaxFeeRange,
  getDiscountRange,
  triggerAdvisoryFeeAlert,
  calcAnnualMaxFee,
  calcDiscount,
  getAnnualFeeTypeOptions,
  isHistoricManagerFee,
  setAdvisoryFeeDataForSecurity,
} from '../shared/shared-advisory-fee-helpers';
import { annualFeeChargeFrequencies } from '../../utils/input-options';
import { FEE_CONFIG_TYPE, FEE_TYPES } from '../../utils/constants';

const { ONLY_FEE_CHOICE, EVERYTHING_ON } = FEE_CONFIG_TYPE;
const { MAX_OR_FEE_SCHEDULE, FLAT } = FEE_TYPES;

export default {
  name: 'advisory-fee-table',
  components: {
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsSelect,
    MdsPopover,
    MoreInfo,
    FeeChoice,
    HypoNumberInput,
    ManagerFeeRender,
    AnnualFeeTotalRender,
    AnnualMaxFeeTotalRender,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    securities: Array,
    showAnnualAssetBaseFee: {
      type: Boolean,
      default: true,
    },
    showAnnualFeeFrequency: {
      type: Boolean,
      default: true,
    },
    assetBasedFeeConfig: {
      type: Object,
      default: () => {},
    },
    environment: String,
    userInfo: Object,
    allFeeScheduleList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      defaultConfig: {
        settings: {
          languageId: this.getComponentConfig().settings.languageId || 'en-US',
        },
      },
      advisoryFeeSecurities: this.securities,
      showFeeScheduleModal: false,
      historicMaxFeeInputPopoverFlags: [],
    };
  },
  provide() {
    return {
      translate: this.translate,
      formatNumber: this.formatNumber,
    };
  },
  computed: {
    defaultVals() {
      return this.settings?.preference;
    },
    headers() {
      if (!this.showAnnualAssetBaseFee) {
        return [
          {
            text: this.translate('name'),
            fieldName: 'securityName',
            style: {
              width: '100%',
            },
          },
        ];
      }
      let headers = [
        {
          text: this.translate('name'),
          fieldName: 'securityName',
          style: {
            width: '43%',
          },
        },
      ];
      const width = `${roundNumber(55 / this.assetBaseFeeColspan, 4)}%`;
      if (this.showTypeColumn) {
        headers = headers.concat([
          {
            text: this.translate('type'),
            fieldName: 'annualFeeType',
            style: {
              width,
            },
          },
        ]);
      }
      if (this.showFeeChoiceColumn) {
        const headerLabel = getFeeChoiceLabelKey(this.assetBasedFeeConfig);
        headers = headers.concat([
          {
            text: this.translate(headerLabel),
            fieldName: 'clickToAddColumn',
            style: {
              width,
              maxWidth: '230px',
            },
          },
        ]);
      }
      if (this.showAnnualAmountColumn) {
        headers = headers.concat([
          {
            text: this.settings.enableThreeReturns
              ? this.translate('annualProposedFeePercent')
              : this.translate('annualFee'),
            fieldName: 'annualFeeAmount',
            style: {
              width,
            },
          },
        ]);
      }
      if (this.showDiscountColumn) {
        headers = headers.concat([
          {
            text: this.translate('discount'),
            fieldName: 'discount',
            style: {
              width,
            },
          },
        ]);
      }
      if (this.showManagerFeeExcludedColumns) {
        const managerFeeColumnHeader = {
          ...this.managerFeeColumnHeader,
          style: {
            width,
          },
        };
        if (!this.settings.enableThreeReturns) {
          headers = headers.concat([
            managerFeeColumnHeader,
            {
              ...this.annualFeeTotalColumnHeader,
              style: {
                width,
              },
            },
          ]);
        } else {
          headers = headers.concat([
            managerFeeColumnHeader,
            {
              ...this.annualMaxFeeTotalColumnHeader,
              style: {
                width,
              },
            },
            {
              ...this.annualProposedFeeTotalColumnHeader,
              style: {
                width,
              },
            },
          ]);
        }
      }
      if (this.showAnnualFeeFrequency) {
        headers = headers.concat([
          {
            text: this.translate('billedFrequency'),
            fieldName: 'annualFeeChargeFrequency',
            style: {
              width,
            },
          },
        ]);
      }
      return headers;
    },
    managerFeeColumnHeader() {
      return {
        text: this.translate('managerFeePercent'),
        fieldName: 'managerFee',
        showInfoIcons: this.settings.showInfoIcons,
        infoIconContent: this.translate('managerFeeFieldIconInfo'),
      };
    },
    annualFeeTotalColumnHeader() {
      return {
        text: this.translate('totalAnnualFeePercent'),
        fieldName: 'totalAnnualFee',
        showInfoIcons: this.settings.showInfoIcons,
        infoIconContent: `${this.translate('totalAnnualFeePercent')} = ${this.translate(
          'annualFee'
        )} + ${this.translate('managerFeePercent')}`,
      };
    },
    annualMaxFeeTotalColumnHeader() {
      const annualFeeColumnName = this.showTypeColumn
        ? this.translate('maxFee')
        : this.translate('annualMaxFeePercent');
      return {
        text: this.translate('totalAnnualMaxFeePercent'),
        fieldName: 'totalAnnualMaxFee',
        showInfoIcons: this.settings.showInfoIcons,
        infoIconContent: `${this.translate(
          'totalAnnualMaxFeePercent'
        )} = ${annualFeeColumnName} + ${this.translate('managerFeePercent')}`,
      };
    },
    annualProposedFeeTotalColumnHeader() {
      return {
        text: this.translate('totalAnnualProposedFeePercent'),
        fieldName: 'totalAnnualProposedFee',
        showInfoIcons: this.settings.showInfoIcons,
        infoIconContent: `${this.translate('totalAnnualProposedFeePercent')} = ${this.translate(
          'annualProposedFeePercent'
        )} + ${this.translate('managerFeePercent')}`,
      };
    },
    showAssetBaseFee() {
      return (
        this.showAnnualAssetBaseFee &&
        (this.showFeeChoiceColumn || this.showAnnualAmountColumn || this.showAnnualFeeFrequency)
      );
    },
    showTypeColumn() {
      return this.assetBasedFeeConfig.assetBasedFeeConfigType === EVERYTHING_ON;
    },
    showFeeChoiceColumn() {
      const { assetBasedFeeConfigType } = this.assetBasedFeeConfig;
      const existMaxOrFeeScheduleType = this.advisoryFeeSecurities.some(security =>
        this.isMaxOrScheduleType(security)
      );
      return (
        assetBasedFeeConfigType === ONLY_FEE_CHOICE ||
        (assetBasedFeeConfigType === EVERYTHING_ON && existMaxOrFeeScheduleType)
      );
    },
    showAnnualAmountColumn() {
      return this.advisoryFeeSecurities.some(security => {
        const feeSchedule = security?.advisoryFeeSchedule;
        const validMaxFee =
          (isMaxFee(feeSchedule) || isHistoricMaxFee(feeSchedule)) && feeSchedule?.allowMaxDiscount;
        return this.isFlat(security) || validMaxFee;
      });
    },
    showDiscountColumn() {
      return this.advisoryFeeSecurities.some(security => {
        return (
          security?.advisoryFeeSchedule?.allowMaxDiscount &&
          !security?.advisoryFeeSchedule?.isHistoricMaxFee
        );
      });
    },
    showManagerFeeExcludedColumns() {
      return this.advisoryFeeSecurities.some(security => {
        return (
          ['PM', 'SA'].includes(security.securityType) &&
          security?.advisoryFeeSchedule?.separateAccountManagerFee
        );
      });
    },
    assetBaseFeeColspan() {
      let colspan = 0;
      if (this.showTypeColumn) {
        colspan += 1;
      }
      if (this.showFeeChoiceColumn) {
        colspan += 1;
      }
      if (this.showAnnualAmountColumn) {
        colspan += 1;
      }
      if (this.showDiscountColumn) {
        colspan += 1;
      }
      if (this.showAnnualFeeFrequency) {
        colspan += 1;
      }
      if (this.showManagerFeeExcludedColumns) {
        colspan += this.settings.enableThreeReturns ? 3 : 2;
      }
      return colspan;
    },
    annualFeeTypeOptions() {
      const data = getAnnualFeeTypeOptions(this.assetBasedFeeConfig);
      return data.map(({ holdingLabelKey, value }) => {
        return {
          text: this.translate(holdingLabelKey),
          value,
        };
      });
    },
    billFrequencyOptions() {
      return annualFeeChargeFrequencies.map(({ labelKey, value }) => {
        return {
          text: this.translate(labelKey),
          value,
        };
      });
    },
    isEqualAnnualFeeType() {
      const firstAnnualFeeType = this.advisoryFeeSecurities[0]?.fees?.annualFeeType;
      return (
        typeof firstAnnualFeeType !== 'undefined' &&
        this.advisoryFeeSecurities.every(
          security => security?.fees?.annualFeeType === firstAnnualFeeType
        )
      );
    },
  },
  watch: {
    securities: {
      handler() {
        this.advisoryFeeSecurities = this.securities;
      },
      deep: true,
    },
    allFeeScheduleList: {
      handler() {
        this.advisoryFeeSecurities = this.advisoryFeeSecurities.map(security => {
          return setAdvisoryFeeDataForSecurity(
            security,
            security?.fees?.feeScheduleId ?? '',
            this.allFeeScheduleList,
            this.defaultVals.discount
          );
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.historicMaxFeeInputPopoverFlags = Array.from(
      { length: this.advisoryFeeSecurities.length },
      () => false
    );
  },
  methods: {
    isFlat(security) {
      return security?.fees?.annualFeeType === FLAT;
    },
    isMaxFee(security) {
      return isMaxFee(security?.advisoryFeeSchedule);
    },
    isHistoricMaxFee(security) {
      return isHistoricMaxFee(security?.advisoryFeeSchedule);
    },
    isHistoricMaxFeeDiscountApplicable(security) {
      return this.isHistoricMaxFee(security) && security?.advisoryFeeSchedule?.allowMaxDiscount;
    },
    isHistoricManagerFee(security) {
      return isHistoricManagerFee(security?.historicManagerFee);
    },
    isTotalAnnualFeeApplicable(security) {
      return this.isHistoricMaxFee(security) || this.isHistoricManagerFee(security);
    },
    getFlatManagerFeeAmount(security) {
      return security?.managerFee;
    },
    getHistoricManagerFee(security) {
      return security?.historicManagerFee;
    },
    isMaxFeeReadOnly(security) {
      const feeSchedule = security?.advisoryFeeSchedule;
      return isMaxFee(feeSchedule) && feeSchedule.allowMaxDiscount === false;
    },
    isDiscountAllowedForMaxOrSchedule(security) {
      return (
        this.isMaxOrScheduleType(security) &&
        security?.advisoryFeeSchedule?.allowMaxDiscount &&
        !this.isHistoricMaxFee(security)
      );
    },
    isSecurityFeeDataPointExist(security, fieldName) {
      return typeof security?.fees?.[fieldName] !== 'undefined';
    },
    getMaxFee(security) {
      const { annualMaxFee } = security?.fees ?? {};
      if (typeof annualMaxFee !== 'undefined') {
        return annualMaxFee;
      }
      return security?.advisoryFeeSchedule?.maxFee;
    },
    getUserInputFeeForHistoricMaxFee(security) {
      const { annualMaxFee } = security?.fees ?? {};
      return annualMaxFee;
    },
    getMaxFeeRange(security) {
      return getMaxFeeRange(security?.advisoryFeeSchedule);
    },
    getHistoricMaxFeeRange(security) {
      return getHistoricMaxFeeRange(security?.advisoryFeeSchedule);
    },
    getDiscountRange(security) {
      return getDiscountRange(security?.advisoryFeeSchedule);
    },
    getDiscount(security) {
      const { discount } = security?.fees ?? {};
      if (typeof discount !== 'undefined') {
        return discount;
      }
      return 0;
    },
    handleSecurityAnnualFeeTypeChange(security) {
      security.fees.annualFeeAmount = this.defaultVals.annualFeeAmount;
      security.fees.feeScheduleId = this.defaultVals.feeScheduleId;
      security.fees.annualMaxFee = this.defaultVals.annualMaxFee;
      security.fees.discount = this.defaultVals.discount;
      this.$delete(security, 'advisoryFeeSchedule');
      this.emitSecuritiesData();
    },
    handleSecurityAnnualFeePercentChanged(annualFeePercentage, security) {
      security.fees = { ...security.fees, annualFeeAmount: annualFeePercentage };
      this.emitSecuritiesData();
    },
    handleSecurityMaxFeeChanged(annualMaxFee, security) {
      security.fees.discount = undefined;
      security.fees.annualMaxFee = annualMaxFee;
      if (this.isMaxFee(security)) {
        security.fees.discount = calcDiscount(security.advisoryFeeSchedule?.maxFee, annualMaxFee);
      }
      this.emitSecuritiesData();
    },
    handleSecurityFeeDiscountChanged(discount, security) {
      security.fees.discount = discount;
      if (isMaxFee(security?.advisoryFeeSchedule)) {
        security.fees.annualMaxFee = calcAnnualMaxFee(
          security.advisoryFeeSchedule.maxFee,
          discount
        );
      }
      this.emitSecuritiesData();
    },
    triggerAlert(security, fieldName) {
      triggerAdvisoryFeeAlert(security?.advisoryFeeSchedule, fieldName, this);
    },
    emitSecuritiesData() {
      this.$emit('security-advisory-fee-changed', this.advisoryFeeSecurities);
    },
    getSecurityFeeSchedule(security) {
      return security?.fees?.feeScheduleId;
    },
    isMaxOrScheduleType(security) {
      return security?.fees?.annualFeeType === MAX_OR_FEE_SCHEDULE;
    },
    handleFeeScheduleChange(feeScheduleId, securityId) {
      const securityIndex = this.advisoryFeeSecurities.findIndex(
        sec => sec.securityId === securityId
      );
      this.advisoryFeeSecurities[securityIndex] = setAdvisoryFeeDataForSecurity(
        this.advisoryFeeSecurities[securityIndex],
        feeScheduleId,
        this.allFeeScheduleList,
        this.defaultVals.discount
      );
      this.emitSecuritiesData();
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import '@mds/icon/src/icon_mixins';

.advisory-fee-table {
  table-layout: auto;
  &__header-cell .mds-th__text {
    display: flex;
  }
}

.security-name__wrapper {
  display: inline-flex;
  align-items: center;
  .alert-icon {
    margin-right: $mds-space-half-x;
    fill: red;
    stroke: red;
  }
}

.click-to-add__wrapper {
  .plus-icon {
    .mds-icon,
    .mds-icon:hover {
      @include mds-icon-color($mds-color-blue-36);
    }
  }
}
</style>
